import type {
  AdminBattleRoyaleMapsResponse,
  AdminMapListResponse,
  AdminMapRawResponse,
  AdminPlayerDetailResponse,
  AdminPlayerSearchResponse,
  AdminRandomMapsResponse,
  AdminSiegeMapsResponse,
  AdminUploadMapRequest,
  AdminUploadMapResponse,
  AuthResponse,
  BattleRoyaleConfigResponse,
  CreateGameRequest,
  CreateGameResponse,
  GameListResponse,
  LeaderboardResponse,
  LoginRequest,
  MapListResponse,
  MatchmadeMode,
  MatchmakeResponse,
  MeResponse,
  MinLobbyConfigResponse,
  RegisterRequest,
  SiegeConfigResponse,
  TurboConfigResponse,
  UpdateBattleRoyaleConfigRequest,
  UpdateBattleRoyaleMapsRequest,
  UpdateRandomMapsRequest,
  UpdateSiegeConfigRequest,
  UpdateSiegeMapsRequest,
  UpdateMinLobbyConfigRequest,
  UpdateTurboConfigRequest,
  UpdateVersionsRequest,
  UpdateWarmupConfigRequest,
  VersionsResponse,
  WarmupConfigResponse,
} from "@webolo/shared";

async function request<T>(
  path: string,
  init: RequestInit & { expectError?: boolean } = {},
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { error: `http_${res.status}` };
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : `http_${status}`);
    this.name = "ApiError";
  }
}

export const api = {
  register: (req: RegisterRequest) =>
    request<AuthResponse>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  login: (req: LoginRequest) =>
    request<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  logout: () =>
    request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  me: () => request<MeResponse>("/api/auth/me"),
  leaderboard: (limit = 50) =>
    request<LeaderboardResponse>(`/api/leaderboard?limit=${limit}`),
  listGames: () => request<GameListResponse>("/api/games"),
  listMaps: () => request<MapListResponse>("/api/maps"),
  createGame: (req: CreateGameRequest) =>
    request<CreateGameResponse>("/api/games", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  matchmake: (mode: MatchmadeMode) =>
    request<MatchmakeResponse>("/api/matchmake", {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  versions: () => request<VersionsResponse>("/api/versions"),
  turboConfig: () => request<TurboConfigResponse>("/api/turbo"),
  siegeConfig: () => request<SiegeConfigResponse>("/api/siege"),
  battleRoyaleConfig: () => request<BattleRoyaleConfigResponse>("/api/battle-royale"),
  adminMe: () => request<{ username: string }>("/api/admin/me"),
  adminGetTurbo: () => request<TurboConfigResponse>("/api/admin/turbo"),
  adminUpdateTurbo: (req: UpdateTurboConfigRequest) =>
    request<TurboConfigResponse>("/api/admin/turbo", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetVersions: () => request<VersionsResponse>("/api/admin/versions"),
  adminUpdateVersions: (req: UpdateVersionsRequest) =>
    request<VersionsResponse>("/api/admin/versions", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminListMaps: () => request<AdminMapListResponse>("/api/admin/maps"),
  adminUploadMap: (req: AdminUploadMapRequest) =>
    request<AdminUploadMapResponse>("/api/admin/maps", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  adminDeleteMap: (id: string) =>
    request<{ ok: true }>(`/api/admin/maps/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  adminGetMapRaw: (id: string) =>
    request<AdminMapRawResponse>(`/api/admin/maps/${encodeURIComponent(id)}/raw`),
  adminGetRandomMaps: () => request<AdminRandomMapsResponse>("/api/admin/random-maps"),
  adminUpdateRandomMaps: (req: UpdateRandomMapsRequest) =>
    request<AdminRandomMapsResponse>("/api/admin/random-maps", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetSiege: () => request<SiegeConfigResponse>("/api/admin/siege"),
  adminUpdateSiege: (req: UpdateSiegeConfigRequest) =>
    request<SiegeConfigResponse>("/api/admin/siege", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetSiegeMaps: () => request<AdminSiegeMapsResponse>("/api/admin/siege-maps"),
  adminUpdateSiegeMaps: (req: UpdateSiegeMapsRequest) =>
    request<AdminSiegeMapsResponse>("/api/admin/siege-maps", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetBattleRoyale: () => request<BattleRoyaleConfigResponse>("/api/admin/battle-royale"),
  adminUpdateBattleRoyale: (req: UpdateBattleRoyaleConfigRequest) =>
    request<BattleRoyaleConfigResponse>("/api/admin/battle-royale", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetBattleRoyaleMaps: () => request<AdminBattleRoyaleMapsResponse>("/api/admin/battle-royale-maps"),
  adminUpdateBattleRoyaleMaps: (req: UpdateBattleRoyaleMapsRequest) =>
    request<AdminBattleRoyaleMapsResponse>("/api/admin/battle-royale-maps", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetWarmup: () => request<WarmupConfigResponse>("/api/admin/warmup"),
  adminUpdateWarmup: (req: UpdateWarmupConfigRequest) =>
    request<WarmupConfigResponse>("/api/admin/warmup", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminGetMinLobby: () => request<MinLobbyConfigResponse>("/api/admin/min-lobby"),
  adminUpdateMinLobby: (req: UpdateMinLobbyConfigRequest) =>
    request<MinLobbyConfigResponse>("/api/admin/min-lobby", {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  adminSearchPlayers: (q: string) =>
    request<AdminPlayerSearchResponse>(`/api/admin/players?q=${encodeURIComponent(q)}`),
  adminGetPlayer: (id: number) =>
    request<AdminPlayerDetailResponse>(`/api/admin/players/${id}`),
  adminGrantPremium: (userId: number) =>
    request<{ ok: true; premium: boolean }>("/api/admin/premium/grant", {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  adminRevokePremium: (userId: number) =>
    request<{ ok: true; premium: boolean }>("/api/admin/premium/revoke", {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
};
